import { Trophy, Clock, Lock, CheckCircle2, Calendar } from "lucide-react";
import type { PickemEvent } from "@api/generated/types.gen";
import { cn } from "@/lib/utils";
import {
  effectiveStatusBadgeClass,
  effectiveStatusLabel,
  formatRelativeTime,
  getEffectiveStatus,
} from "../lib/pickem-status";
import PickemPlaceholderUrl from "../assets/pickem-placeholder.webp?url";

interface PickemEventCardProps {
  event: PickemEvent;
  onClick?: (event: PickemEvent) => void;
}

const iconByStatus = {
  draft: Trophy,
  scheduled: Calendar,
  active: Clock,
  locked: Lock,
  completed: CheckCircle2,
} as const;

export function PickemEventCard({ event, onClick }: PickemEventCardProps) {
  const status = getEffectiveStatus(event);
  const StatusIcon = iconByStatus[status];

  const lockDate = event.lockDate ? new Date(event.lockDate) : null;
  const publishDate = event.publishDate ? new Date(event.publishDate) : null;

  let timingLine: string | null = null;
  if (status === "scheduled") {
    timingLine = `Opens ${formatRelativeTime(publishDate) ?? "soon"}`;
  } else if (status === "active") {
    const rel = formatRelativeTime(lockDate);
    timingLine = rel ? `Locks ${rel}` : "Open for picks";
  } else if (status === "locked") {
    timingLine = "Picks locked — awaiting results";
  } else if (status === "completed") {
    timingLine = "Event complete";
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(event)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.(event);
      }}
      className={cn(
        "bg-card text-card-foreground rounded-xl border shadow-sm overflow-hidden",
        "hover:bg-accent/40 hover:border-primary/40 transition-colors cursor-pointer group",
      )}
    >
      <div className="aspect-[16/9] relative overflow-hidden bg-muted">
        <img
          src={event.bannerUrl || event.imageUrl || PickemPlaceholderUrl}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          draggable={false}
        />
        <div
          className={cn(
            "absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
            effectiveStatusBadgeClass[status],
          )}
        >
          <StatusIcon className="h-3 w-3" />
          {effectiveStatusLabel[status]}
        </div>
      </div>
      <div className="p-4 space-y-2">
        {event.logoUrl && (
          <img
            src={event.logoUrl}
            alt={`${event.name} logo`}
            className="h-12 w-12 rounded bg-background object-contain"
          />
        )}
        <h3 className="font-semibold text-lg line-clamp-1">{event.name}</h3>
        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {event.description}
          </p>
        )}
        {timingLine && (
          <p className="text-xs text-muted-foreground pt-1">{timingLine}</p>
        )}
      </div>
    </div>
  );
}
